import type { VariantProps } from 'class-variance-authority';
import type { HTMLAttributes } from 'vue';
import { cva } from 'class-variance-authority';
import type { PButtonVariants } from '@ui/components/core/p-button/index.js';
import { cn } from '@ui/lib/utils.js';

export { default as PInputGroup } from './PInputGroup.vue';
export { default as PInputGroupAddon } from './PInputGroupAddon.vue';
export { default as PInputGroupButton } from './PInputGroupButton.vue';
export { default as PInputGroupInput } from './PInputGroupInput.vue';
export { default as PInputGroupText } from './PInputGroupText.vue';
export { default as PInputGroupTextarea } from './PInputGroupTextarea.vue';

export const inputGroupAddonVariants = cva(
    cn(
        'text-foreground-muted',
        'flex',
        'h-auto',
        'cursor-text',
        'items-center',
        'justify-center',
        'gap-2',
        'py-1.5',
        'paragraph-sm',
        'font-medium',
        'select-none',
        `[&>svg:not([class*='size-'])]:size-4`,
        '[&>kbd]:rounded-[calc(var(--radius)-5px)]',
        'group-data-[disabled=true]/input-group:opacity-50',
    ),
    {
        variants: {
            align: {
                'inline-start': 'order-first pl-3 has-[>button]:ml-[-0.45rem] has-[>kbd]:ml-[-0.35rem]',
                'inline-end': 'order-last pr-3 has-[>button]:mr-[-0.45rem] has-[>kbd]:mr-[-0.35rem]',
                'block-start':
                    'order-first w-full justify-start px-3 pt-3 [.border-b]:pb-3 group-has-[>input]/input-group:pt-2.5',
                'block-end':
                    'order-last w-full justify-start px-3 pb-3 [.border-t]:pt-3 group-has-[>input]/input-group:pb-2.5',
            },
        },
        defaultVariants: {
            align: 'inline-start',
        },
    },
);

export type InputGroupVariants = VariantProps<typeof inputGroupAddonVariants>;

export const inputGroupButtonVariants = cva('text-sm shadow-none flex gap-2 items-center', {
    variants: {
        size: {
            xs: "h-6 gap-1 px-2 rounded-[calc(var(--radius)-5px)] [&>svg:not([class*='size-'])]:size-3.5 has-[>svg]:px-2",
            sm: 'h-8 px-2.5 gap-1.5 rounded-md has-[>svg]:px-2.5',
            'icon-xs': 'size-6 rounded-[calc(var(--radius)-5px)] p-0 has-[>svg]:p-0',
            'icon-sm': 'size-8 p-0 has-[>svg]:p-0',
        },
    },
    defaultVariants: {
        size: 'xs',
    },
});

export type InputGroupButtonVariants = VariantProps<typeof inputGroupButtonVariants>;

export interface InputGroupButtonProps {
    variant?: PButtonVariants['variant'];
    size?: InputGroupButtonVariants['size'];
    class?: HTMLAttributes['class'];
}
